import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class SafeHtmlService {
  private readonly allowedTags = new Set(['A', 'SPAN', 'IMG', 'BR']);
  private readonly allowedAttributes: Record<string, Set<string>> = {
    A: new Set(['href', 'target', 'rel', 'class', 'data-sihc-link-url', 'data-url']),
    SPAN: new Set(['class']),
    IMG: new Set(['class', 'src', 'alt', 'loading']),
    BR: new Set<string>()
  };

  /**
   * Sanitizes server-generated link and preview HTML before assigning to innerHTML.
   */
  public sanitizeFormattedHtml(html: string): string {
    if (!html) {
      return '';
    }

    const template = document.createElement('template');
    template.innerHTML = html;
    this.sanitizeChildren(template.content);
    return template.innerHTML;
  }

  private sanitizeChildren(parent: ParentNode): void {
    for (const node of [...parent.childNodes]) {
      if (node.nodeType === Node.TEXT_NODE) {
        continue;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        node.remove();
        continue;
      }

      const element = node as HTMLElement;
      const tagName = element.tagName;

      if (!this.allowedTags.has(tagName)) {
        element.remove();
        continue;
      }

      for (const attribute of [...element.attributes]) {
        const name = attribute.name.toLowerCase();
        if (!this.allowedAttributes[tagName]?.has(name)) {
          element.removeAttribute(attribute.name);
        }
      }

      if (tagName === 'A') {
        const href = element.getAttribute('href');
        if (!this.isSafeHttpsUrl(href)) {
          element.removeAttribute('href');
        }
      }

      if (tagName === 'IMG') {
        const src = element.getAttribute('src');
        if (!this.isSafeHttpsUrl(src)) {
          element.remove();
          continue;
        }
      }

      this.sanitizeChildren(element);
    }
  }

  private isSafeHttpsUrl(value: string | null): boolean {
    return !!value && /^https:\/\//i.test(value.trim());
  }
}
